/* eslint-disable react/jsx-no-bind */
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import InfoIcon from '@mui/icons-material/Info';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Avatar,
  Badge,
  Button,
  IconButton,
  TextField,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import { tokens } from '../../theme';
import { formatToIST, formatToISTCustom } from '../../utils/dateFormatter';
import logger from '../../utils/logger';
import Header from '../Header';

import BookingAlertsService from './bookingAlertsService';

const BookingAlerts = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Staged filter values (what user is typing)
  const [filter, setFilter] = useState('');
  const [tripId, setTripId] = useState('');
  const [shipmentId, setShipmentId] = useState('');
  const [tripShipmentId, setTripShipmentId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Applied filter values (actually used in API calls)
  const [appliedFilter, setAppliedFilter] = useState('');
  const [appliedTripId, setAppliedTripId] = useState('');
  const [appliedShipmentId, setAppliedShipmentId] = useState('');
  const [appliedTripShipmentId, setAppliedTripShipmentId] = useState('');
  const [appliedFromDate, setAppliedFromDate] = useState('');
  const [appliedToDate, setAppliedToDate] = useState('');

  const filterOptions = [
    { value: '', label: 'All Alerts' },
    { value: 'no traveller assigned', label: 'No Traveller Assigned' },
    { value: 'pickup delayed', label: 'Pickup Delayed' },
    { value: 'traveller not moving', label: 'Traveller Not Moving' },
    { value: 'booking cancelled', label: 'Booking Cancelled' },
  ];

  const getAlertColor = (alertType) => {
    const lowerAlert = alertType?.toLowerCase() || '';
    if (lowerAlert.includes('cancelled')) return 'warning';
    if (lowerAlert.includes('delayed')) return 'warning';
    if (lowerAlert.includes('not moving')) return 'warning';
    if (lowerAlert.includes('no traveller')) return 'warning';
    return 'info';
  };

  const getAlertIcon = (alertType) => {
    const lowerAlert = alertType?.toLowerCase() || '';
    if (lowerAlert.includes('cancelled'))
      return <WarningAmberIcon sx={{ fontSize: '1.1rem', mr: 0.5 }} />;
    if (lowerAlert.includes('delayed'))
      return <WarningAmberIcon sx={{ fontSize: '1.1rem', mr: 0.5 }} />;
    if (lowerAlert.includes('not moving'))
      return <WarningAmberIcon sx={{ fontSize: '1.1rem', mr: 0.5 }} />;
    return <InfoIcon sx={{ fontSize: '1.1rem', mr: 0.5 }} />;
  };

  const getStatusColor = (status) => {
    if (!status) return 'default';
    const upperStatus = status.toUpperCase();
    if (upperStatus === 'UNASSIGNED') return 'warning';
    if (upperStatus.includes('PENDING')) return 'warning';
    if (upperStatus.includes('COMPLETED')) return 'success';
    return 'info';
  };

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await BookingAlertsService.fetchAlerts(
        page + 1,
        rowsPerPage,
        appliedFilter,
        appliedTripId,
        appliedShipmentId,
        appliedTripShipmentId,
        appliedFromDate,
        appliedToDate,
      );
      // Defensive check for API response structure
      const alertsData = response.data?.result || [];
      const total = response.data?.totalCount || 0;
      setAlerts(Array.isArray(alertsData) ? alertsData : []);
      setTotalCount(typeof total === 'number' ? total : 0);
    } catch (err) {
      toast.error('Failed to fetch booking alerts');
      logger.error('Error fetching alerts:', err);
      setAlerts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    rowsPerPage,
    appliedFilter,
    appliedTripId,
    appliedShipmentId,
    appliedTripShipmentId,
    appliedFromDate,
    appliedToDate,
  ]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (event) => {
    setFilter(event.target.value);
  };

  const handleApplyFilters = () => {
    // Apply staged filters
    setAppliedFilter(filter);
    setAppliedTripId(tripId);
    setAppliedShipmentId(shipmentId);
    setAppliedTripShipmentId(tripShipmentId);
    setAppliedFromDate(fromDate);
    setAppliedToDate(toDate);
    setPage(0); // Reset to first page when applying filters
  };

  const handleClearAllFilters = () => {
    // Clear staged filters
    setFilter('');
    setTripId('');
    setShipmentId('');
    setTripShipmentId('');
    setFromDate('');
    setToDate('');

    // Clear applied filters
    setAppliedFilter('');
    setAppliedTripId('');
    setAppliedShipmentId('');
    setAppliedTripShipmentId('');
    setAppliedFromDate('');
    setAppliedToDate('');
    setPage(0);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="400px"
      >
        <CircularProgress
          sx={{
            color:
              theme.palette.mode === 'dark'
                ? colors.swiftcarry[100]
                : 'rgba(0, 201, 150, 0.7)',
          }}
        />
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Header
        title="Booking Alerts"
        subtitle="Monitor booking-related alerts and issues"
      />

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: '16px',
          backgroundColor: colors.primary[400],
          border: `1px solid ${colors.grey[700]}`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          gap={2}
        >
          {/* Filter Section */}
          <Box
            display="flex"
            alignItems="center"
            gap={1.5}
            flex={1}
            flexWrap="wrap"
          >
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: '12px',
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? colors.primary[500]
                    : 'rgba(0, 201, 150, 0.05)',
                border: `1px solid ${
                  theme.palette.mode === 'dark'
                    ? colors.grey[700]
                    : 'rgba(0, 201, 150, 0.2)'
                }`,
              }}
            >
              <FilterListIcon
                sx={{
                  color:
                    theme.palette.mode === 'dark'
                      ? colors.swiftcarry[100]
                      : 'rgba(0, 201, 150, 0.7)',
                  fontSize: '1.3rem',
                }}
              />
              <Typography
                variant="subtitle2"
                color={colors.grey[200]}
                sx={{ fontWeight: 600, fontSize: '0.875rem' }}
              >
                Filter
              </Typography>
            </Box>

            <FormControl
              sx={{
                minWidth: { xs: '100%', sm: 240, md: 300 },
                flex: { xs: '1 1 100%', sm: '0 1 auto' },
                position: 'relative',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? colors.primary[500]
                      : '#ffffff',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  '& fieldset': {
                    borderColor: colors.grey[700],
                    borderWidth: '1.5px',
                  },
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.08)',
                    '& fieldset': {
                      borderColor:
                        theme.palette.mode === 'dark'
                          ? colors.swiftcarry[100]
                          : 'rgba(0, 201, 150, 0.5)',
                    },
                  },
                  '&.Mui-focused': {
                    boxShadow: `0 0 0 3px ${
                      theme.palette.mode === 'dark'
                        ? 'rgba(0, 201, 150, 0.12)'
                        : 'rgba(0, 201, 150, 0.08)'
                    }`,
                    '& fieldset': {
                      borderColor:
                        theme.palette.mode === 'dark'
                          ? colors.swiftcarry[100]
                          : 'rgba(0, 201, 150, 0.6)',
                      borderWidth: '2px',
                    },
                  },
                },
                '& .MuiInputLabel-root': {
                  color: colors.grey[300],
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  '&.Mui-focused': {
                    color:
                      theme.palette.mode === 'dark'
                        ? colors.swiftcarry[100]
                        : 'rgba(0, 201, 150, 0.8)',
                    fontWeight: 600,
                  },
                },
                '& .MuiSelect-icon': {
                  color: colors.grey[300],
                  fontSize: '1.5rem',
                  transition: 'transform 0.3s ease, color 0.3s ease',
                },
                '& .MuiSelect-iconOpen': {
                  transform: 'rotate(180deg)',
                  color:
                    theme.palette.mode === 'dark'
                      ? colors.swiftcarry[100]
                      : 'rgba(0, 201, 150, 0.7)',
                },
              }}
            >
              <InputLabel id="filter-label" shrink>
                Filter by alert type
              </InputLabel>
              <Select
                labelId="filter-label"
                value={filter}
                label="Filter by alert type"
                onChange={handleFilterChange}
                IconComponent={ExpandMoreIcon}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{ color: colors.grey[400] }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.875rem',
                            fontStyle: 'italic',
                          }}
                        >
                          Select alert type
                        </Typography>
                      </Box>
                    );
                  }
                  const selectedOption = filterOptions.find(
                    (opt) => opt.value === selected,
                  );
                  return (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={selectedOption?.label || selected}
                        size="small"
                        color={getAlertColor(selected)}
                        sx={{
                          height: '24px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          '& .MuiChip-label': {
                            px: 1.5,
                          },
                        }}
                      />
                    </Box>
                  );
                }}
                sx={{
                  '& .MuiSelect-select': {
                    py: 1.5,
                    px: 2,
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: '24px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor:
                      theme.palette.mode === 'dark'
                        ? colors.swiftcarry[100]
                        : 'rgba(0, 201, 150, 0.5)',
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '12px',
                      mt: 1,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                      border: `1px solid ${colors.grey[700]}`,
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? colors.primary[500]
                          : '#ffffff',
                      '& .MuiMenuItem-root': {
                        px: 2,
                        py: 1.25,
                        fontSize: '0.875rem',
                        borderRadius: '8px',
                        mx: 1,
                        my: 0.5,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? colors.primary[400]
                              : 'rgba(0, 201, 150, 0.06)',
                          transform: 'translateX(4px)',
                        },
                        '&.Mui-selected': {
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? colors.primary[400]
                              : 'rgba(0, 201, 150, 0.08)',
                          color:
                            theme.palette.mode === 'dark'
                              ? colors.swiftcarry[100]
                              : 'rgba(0, 201, 150, 0.9)',
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? colors.primary[400]
                                : 'rgba(0, 201, 150, 0.12)',
                          },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: '3px',
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? colors.swiftcarry[100]
                                : 'rgba(0, 201, 150, 0.7)',
                            borderRadius: '0 4px 4px 0',
                          },
                        },
                        '& .MuiListItemIcon-root': {
                          minWidth: 36,
                        },
                      },
                    },
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
              >
                {filterOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 16,
                        display: filter === option.value ? 'block' : 'none',
                      },
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <Box display="flex" alignItems="center" gap={1.5}>
                        {getAlertIcon(option.value || option.label)}
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: filter === option.value ? 600 : 500,
                            color:
                              filter === option.value
                                ? theme.palette.mode === 'dark'
                                  ? colors.swiftcarry[100]
                                  : 'rgba(0, 201, 150, 0.9)'
                                : 'inherit',
                          }}
                        >
                          {option.label}
                        </Typography>
                      </Box>
                      {filter === option.value && (
                        <CheckIcon
                          sx={{
                            fontSize: '1.2rem',
                            color:
                              theme.palette.mode === 'dark'
                                ? colors.swiftcarry[100]
                                : 'rgba(0, 201, 150, 0.8)',
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {filter && (
              <IconButton
                onClick={() => {
                  setFilter('');
                }}
                sx={{
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? colors.primary[500]
                      : '#ffffff',
                  border: `1px solid ${colors.grey[700]}`,
                  borderRadius: '12px',
                  '&:hover': {
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? colors.primary[600]
                        : '#f5f5f5',
                    borderColor:
                      theme.palette.mode === 'dark'
                        ? colors.swiftcarry[100]
                        : 'rgba(0, 201, 150, 0.5)',
                  },
                }}
                title="Clear status filter"
              >
                <ClearIcon
                  sx={{
                    fontSize: '1.2rem',
                    color: colors.grey[300],
                  }}
                />
              </IconButton>
            )}
          </Box>

          {/* Stats Section */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2.5,
                py: 1.5,
                borderRadius: '12px',
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? colors.primary[500]
                    : '#ffffff',
                border: `1px solid ${colors.grey[700]}`,
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <Typography
                  variant="caption"
                  color={colors.grey[400]}
                  sx={{
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: 600,
                  }}
                >
                  Total Alerts
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                  <AssignmentIcon
                    sx={{
                      color:
                        theme.palette.mode === 'dark'
                          ? colors.swiftcarry[100]
                          : 'rgba(0, 201, 150, 0.7)',
                      fontSize: '1.2rem',
                    }}
                  />
                  <Typography
                    variant="h6"
                    color={colors.grey[100]}
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.25rem',
                    }}
                  >
                    {totalCount}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Additional filters: IDs and date range */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            gap: 2,
            mt: 2,
            pt: 2,
            borderTop: `1px solid ${colors.grey[700]}`,
          }}
        >
          <TextField
            label="Trip ID"
            type="number"
            value={tripId}
            onChange={(e) => setTripId(e.target.value)}
            variant="outlined"
            size="small"
            placeholder="Trip ID"
            inputProps={{ min: 1, step: 1 }}
            sx={{
              minWidth: { xs: '100%', sm: 120 },
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? colors.primary[500]
                    : '#ffffff',
                '& fieldset': { borderColor: colors.grey[700] },
              },
            }}
          />
          <TextField
            label="Shipment ID"
            type="number"
            value={shipmentId}
            onChange={(e) => setShipmentId(e.target.value)}
            variant="outlined"
            size="small"
            placeholder="Shipment ID"
            inputProps={{ min: 1, step: 1 }}
            sx={{
              minWidth: { xs: '100%', sm: 120 },
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? colors.primary[500]
                    : '#ffffff',
                '& fieldset': { borderColor: colors.grey[700] },
              },
            }}
          />
          <TextField
            label="Booking ID"
            type="number"
            value={tripShipmentId}
            onChange={(e) => setTripShipmentId(e.target.value)}
            variant="outlined"
            size="small"
            placeholder="Booking ID"
            inputProps={{ min: 1, step: 1 }}
            sx={{
              minWidth: { xs: '100%', sm: 140 },
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? colors.primary[500]
                    : '#ffffff',
                '& fieldset': { borderColor: colors.grey[700] },
              },
            }}
          />
          <TextField
            label="From date"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: toDate || undefined }}
            sx={{
              minWidth: { xs: '100%', sm: 160 },
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? colors.primary[500]
                    : '#ffffff',
                '& fieldset': { borderColor: colors.grey[700] },
              },
            }}
          />
          <TextField
            label="To date"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: fromDate || undefined }}
            sx={{
              minWidth: { xs: '100%', sm: 160 },
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? colors.primary[500]
                    : '#ffffff',
                '& fieldset': { borderColor: colors.grey[700] },
              },
            }}
          />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={handleApplyFilters}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? colors.swiftcarry[100]
                    : 'rgba(0, 201, 150, 0.9)',
                color: theme.palette.mode === 'dark' ? '#000' : '#fff',
                '&:hover': {
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? colors.swiftcarry[200]
                      : 'rgba(0, 201, 150, 1)',
                },
              }}
            >
              Apply filters
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearAllFilters}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: colors.grey[600],
                color: colors.grey[300],
                '&:hover': {
                  borderColor: colors.grey[500],
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? colors.primary[500]
                      : 'rgba(0,0,0,0.04)',
                },
              }}
            >
              Clear all
            </Button>
          </Box>
        </Box>

        {/* Page size selector */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mt: 2,
          }}
        >
          <Typography
            variant="body2"
            color={colors.grey[300]}
            sx={{ fontSize: '0.875rem' }}
          >
            Per page:
          </Typography>
          <Select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(0);
            }}
            size="small"
            sx={{
              minWidth: 72,
              borderRadius: '12px',
              backgroundColor:
                theme.palette.mode === 'dark' ? colors.primary[500] : '#ffffff',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.grey[700],
              },
            }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </Box>

        {/* Active filters */}
        {(appliedFilter ||
          appliedTripId ||
          appliedShipmentId ||
          appliedTripShipmentId ||
          appliedFromDate ||
          appliedToDate) && (
          <Box
            mt={2}
            display="flex"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
          >
            <Typography
              variant="caption"
              color={colors.grey[300]}
              sx={{ fontSize: '0.75rem' }}
            >
              Active filters:
            </Typography>
            {appliedFilter && (
              <Chip
                label={
                  filterOptions.find((opt) => opt.value === appliedFilter)
                    ?.label || appliedFilter
                }
                onDelete={() => {
                  setFilter('');
                  setAppliedFilter('');
                  setPage(0);
                }}
                deleteIcon={<ClearIcon sx={{ fontSize: '1rem' }} />}
                color={getAlertColor(appliedFilter)}
                size="small"
                sx={{
                  fontWeight: 600,
                  '& .MuiChip-deleteIcon': {
                    color: 'inherit',
                    '&:hover': { color: 'inherit' },
                  },
                }}
              />
            )}
            {appliedTripId && (
              <Chip
                label={`Trip: ${appliedTripId}`}
                onDelete={() => {
                  setTripId('');
                  setAppliedTripId('');
                  setPage(0);
                }}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            )}
            {appliedShipmentId && (
              <Chip
                label={`Shipment: ${appliedShipmentId}`}
                onDelete={() => {
                  setShipmentId('');
                  setAppliedShipmentId('');
                  setPage(0);
                }}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            )}
            {appliedTripShipmentId && (
              <Chip
                label={`Trip Shipment: ${appliedTripShipmentId}`}
                onDelete={() => {
                  setTripShipmentId('');
                  setAppliedTripShipmentId('');
                  setPage(0);
                }}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            )}
            {appliedFromDate && (
              <Chip
                label={`From: ${formatToISTCustom(
                  appliedFromDate,
                  'DD/MM/YYYY',
                )}`}
                onDelete={() => {
                  setFromDate('');
                  setAppliedFromDate('');
                  setPage(0);
                }}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            )}
            {appliedToDate && (
              <Chip
                label={`To: ${formatToISTCustom(appliedToDate, 'DD/MM/YYYY')}`}
                onDelete={() => {
                  setToDate('');
                  setAppliedToDate('');
                  setPage(0);
                }}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>
        )}
      </Paper>

      <Grid container spacing={3}>
        {alerts.map((alert, index) => (
          <Grid item xs={12} md={6} lg={4} key={`${alert.shipmentId}-${index}`}>
            <Card
              sx={{
                backgroundColor: colors.primary[400],
                borderRadius: '16px',
                border: `1px solid ${colors.grey[700]}`,
                borderLeft: `4px solid ${
                  theme.palette.mode === 'dark'
                    ? colors.swiftcarry[100]
                    : 'rgba(0, 201, 150, 0.6)'
                }`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  transform: 'translateY(-4px)',
                  borderLeft: `4px solid ${
                    theme.palette.mode === 'dark'
                      ? colors.swiftcarry[100]
                      : 'rgba(0, 201, 150, 0.7)'
                  }`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  mb={2}
                >
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <LocalShippingIcon
                        sx={{
                          fontSize: '1.5rem',
                          color:
                            theme.palette.mode === 'dark'
                              ? colors.swiftcarry[100]
                              : 'rgba(0, 201, 150, 0.8)',
                        }}
                      />
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        color={colors.grey[100]}
                        sx={{ fontSize: '1.1rem' }}
                      >
                        Shipment #{alert.shipmentId}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        mt: 1,
                      }}
                    >
                      {!alert.tripId && !alert.tripShipmentId ? (
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '8px',
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? colors.primary[500]
                                : '#f5f5f5',
                            width: 'fit-content',
                          }}
                        >
                          <Chip
                            label={alert.status || 'N/A'}
                            color={getStatusColor(alert.status)}
                            size="small"
                            sx={{
                              height: '24px',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              '& .MuiChip-label': {
                                px: 1,
                              },
                            }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            alignItems: 'center',
                          }}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{
                              px: 1,
                              py: 0.5,
                              borderRadius: '8px',
                              backgroundColor:
                                theme.palette.mode === 'dark'
                                  ? colors.primary[500]
                                  : '#f5f5f5',
                            }}
                          >
                            <DirectionsCarIcon
                              sx={{
                                fontSize: '0.9rem',
                                color: colors.grey[300],
                              }}
                            />
                            <Typography
                              variant="caption"
                              color={colors.grey[200]}
                              sx={{ fontWeight: 500 }}
                            >
                              Trip: {alert.tripId || 'N/A'}
                            </Typography>
                          </Box>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{
                              px: 1,
                              py: 0.5,
                              borderRadius: '8px',
                              backgroundColor:
                                theme.palette.mode === 'dark'
                                  ? colors.primary[500]
                                  : '#f5f5f5',
                            }}
                          >
                            <AssignmentIcon
                              sx={{
                                fontSize: '0.9rem',
                                color: colors.grey[300],
                              }}
                            />
                            <Typography
                              variant="caption"
                              color={colors.grey[200]}
                              sx={{ fontWeight: 500 }}
                            >
                              Booking ID: {alert.tripShipmentId || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <Chip
                    icon={getAlertIcon(alert.bookingAlert)}
                    label={alert.bookingAlert}
                    color={getAlertColor(alert.bookingAlert)}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: '28px',
                      ml: 1,
                      '& .MuiChip-icon': {
                        color: 'inherit',
                      },
                    }}
                  />
                </Box>

                <Divider sx={{ my: 2, borderColor: colors.grey[700] }} />

                <Grid container spacing={2}>
                  {/* Only show Trip User if it exists */}
                  {alert.tripUser && (
                    <Grid item xs={alert.shipmentUser ? 6 : 12}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: '12px',
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? colors.primary[500]
                              : '#f8f9fa',
                          border: `1px solid ${colors.grey[700]}`,
                          height: '100%',
                        }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          mb={1.5}
                        >
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor:
                                theme.palette.mode === 'dark'
                                  ? colors.swiftcarry[100]
                                  : 'rgba(0, 201, 150, 0.8)',
                              color: '#ffffff',
                              fontSize: '0.9rem',
                            }}
                          >
                            <DirectionsCarIcon sx={{ fontSize: '1.1rem' }} />
                          </Avatar>
                          <Typography
                            variant="subtitle2"
                            color={colors.grey[200]}
                            fontWeight="bold"
                            sx={{ fontSize: '0.85rem' }}
                          >
                            Trip User
                          </Typography>
                        </Box>
                        <Box display="flex" flexDirection="column" gap={1}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <PersonIcon
                              sx={{
                                fontSize: '1rem',
                                color: colors.grey[300],
                              }}
                            />
                            <Typography
                              variant="body2"
                              color={colors.grey[100]}
                              sx={{ fontSize: '0.85rem' }}
                            >
                              {alert.tripUser.name || 'N/A'}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <PhoneIcon
                              sx={{
                                fontSize: '1rem',
                                color: colors.grey[300],
                              }}
                            />
                            <Typography
                              variant="body2"
                              color={colors.grey[100]}
                              sx={{ fontSize: '0.85rem' }}
                            >
                              {alert.tripUser.phone || 'N/A'}
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            color={colors.grey[300]}
                            sx={{ fontSize: '0.75rem', ml: 3 }}
                          >
                            ID: {alert.tripUser.userId || 'N/A'}
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  )}

                  {/* Always show Shipment User */}
                  {alert.shipmentUser && (
                    <Grid item xs={alert.tripUser ? 6 : 12}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: '12px',
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? colors.primary[500]
                              : '#f8f9fa',
                          border: `1px solid ${colors.grey[700]}`,
                          height: '100%',
                        }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          mb={1.5}
                        >
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: colors.blueAccent[500],
                              color: '#ffffff',
                              fontSize: '0.9rem',
                            }}
                          >
                            <LocalShippingIcon sx={{ fontSize: '1.1rem' }} />
                          </Avatar>
                          <Typography
                            variant="subtitle2"
                            color={colors.grey[200]}
                            fontWeight="bold"
                            sx={{ fontSize: '0.85rem' }}
                          >
                            Shipment User
                          </Typography>
                        </Box>
                        <Box display="flex" flexDirection="column" gap={1}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <PersonIcon
                              sx={{
                                fontSize: '1rem',
                                color: colors.grey[300],
                              }}
                            />
                            <Typography
                              variant="body2"
                              color={colors.grey[100]}
                              sx={{ fontSize: '0.85rem' }}
                            >
                              {alert.shipmentUser.name || 'N/A'}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <PhoneIcon
                              sx={{
                                fontSize: '1rem',
                                color: colors.grey[300],
                              }}
                            />
                            <Typography
                              variant="body2"
                              color={colors.grey[100]}
                              sx={{ fontSize: '0.85rem' }}
                            >
                              {alert.shipmentUser.phone || 'N/A'}
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            color={colors.grey[300]}
                            sx={{ fontSize: '0.75rem', ml: 3 }}
                          >
                            ID: {alert.shipmentUser.userId || 'N/A'}
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  )}
                </Grid>

                {alert.createdOn && (
                  <Box
                    mt={2}
                    display="flex"
                    justifyContent="flex-start"
                    alignItems="center"
                  >
                    <Typography
                      variant="caption"
                      color={colors.grey[300]}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      Created: {formatToIST(alert.createdOn)}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {alerts.length === 0 && !loading && (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            mt: 4,
            borderRadius: '16px',
            backgroundColor: colors.primary[400],
            border: `1px solid ${colors.grey[700]}`,
          }}
        >
          <InfoIcon
            sx={{
              fontSize: '4rem',
              color: colors.grey[400],
              mb: 2,
            }}
          />
          <Typography variant="h6" color={colors.grey[300]} fontWeight={500}>
            No booking alerts found
          </Typography>
          <Typography variant="body2" color={colors.grey[400]} mt={1}>
            {filter
              ? 'Try adjusting your filter to see more results'
              : 'All booking alerts are resolved'}
          </Typography>
        </Paper>
      )}

      {totalCount > 0 && (
        <Box
          sx={{
            mt: 2,
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#fafafa',
          }}
        >
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            sx={{
              '& .MuiTablePagination-toolbar': {
                padding: '16px 24px',
                minHeight: '64px',
                justifyContent: 'space-between',
                alignItems: 'center',
              },
              '& .MuiTablePagination-selectLabel': {
                marginBottom: 0,
                fontSize: '14px',
                fontWeight: 500,
                color: '#666',
              },
              '& .MuiTablePagination-displayedRows': {
                marginBottom: 0,
                fontSize: '14px',
                fontWeight: 500,
                color: '#666',
              },
              '& .MuiTablePagination-select': {
                borderRadius: '6px',
                border: '1px solid #e0e0e0',
                padding: '4px 8px',
                marginLeft: '8px',
                marginRight: '16px',
                '&:focus': {
                  borderColor: colors.swiftcarry[100],
                  backgroundColor: 'transparent',
                },
              },
              '& .MuiTablePagination-actions': {
                marginLeft: '20px',
                '& .MuiIconButton-root': {
                  padding: '8px',
                  margin: '0 2px',
                  borderRadius: '6px',
                  border: '1px solid #e0e0e0',
                  backgroundColor: 'white',
                  '&:hover': {
                    backgroundColor: colors.swiftcarry[100],
                    borderColor: colors.swiftcarry[100],
                    color: 'white',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#f5f5f5',
                    borderColor: '#e0e0e0',
                    color: '#ccc',
                  },
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default BookingAlerts;
